import PropTypes from "prop-types";
import React from "react";
import { withRouter } from "tools/routerCompat";
import { dateToStringYy, commaFormat } from "components/Shared/Formatters";
import { Map, CellFormatterTooltip, MSpinner, TippedGridCell, TippedIcon } from "components/Shared";

const VIEW_MODE_GRID = "VIEW_MODE_GRID";
const VIEW_MODE_MAP = "VIEW_MODE_MAP";
const _error = window.console.error;
const warningToSuppress =
  "Warning: getDefaultProps is only used on classic React.createClass definitions. Use a static property named `defaultProps` instead.";
export const suppressError = () => {
  window.console.error = errorText => {
    if (errorText && errorText.indexOf(warningToSuppress) >= 0) {
      return null;
    } else {
      _error(errorText);
    }
  };
};
import { DataGrid as ReactDataGrid } from "react-data-grid";
import "react-data-grid/lib/styles.css";
import xlsx from "xlsx";

// Custom implementation to replace react-data-grid-addons Selectors
const filterRows = (state) => {
  const { rows, filters } = state;
  if (!filters || Object.keys(filters).length === 0) {
    return rows || [];
  }
  
  return (rows || []).filter(row => {
    return Object.keys(filters).every(columnKey => {
      const filter = filters[columnKey];
      if (!filter || !filter.filterTerm) return true;
      
      const cellValue = row[columnKey];
      
      // Handle multi-select filters (array of values)
      if (Array.isArray(filter.filterTerm)) {
        if (filter.filterTerm.length === 0) return true;
        const filterValues = filter.filterTerm.map(f => f.value || f);
        return filterValues.includes(cellValue);
      }
      
      // Handle custom filterValues function
      if (filter.filterValues) {
        return filter.filterValues(row, filter, columnKey);
      }
      
      // Handle text filter
      if (cellValue == null) return false;
      return String(cellValue).toLowerCase().includes(String(filter.filterTerm).toLowerCase());
    });
  });
};
import MultiSelectFilter from "components/Search/MultiSelectFilter";
import SearchTextFilter from "components/Search/SearchTextFilter";
import AppInsightsTracker from "components/Shared/AppInsightsTracker";
import DateFilterMenu from "./DateFilterMenu";
import StatusFilterMenu from "./StatusFilterMenu";
import CustomerFilterMenu from "./CustomerFilterMenu";
import InsuranceFilterMenu from "./InsuranceFilterMenu";
import CheckBoxMultiSelect from "./CheckBoxMultiSelect";
import { cloneDeep } from "lodash";

class ResultGrid extends AppInsightsTracker {
  constructor(props) {
    super(props);
    this.displayName = "ResultGrid";
    this.buildGrid = this.buildGrid.bind(this);
    this.downloadGrid = this.downloadGrid.bind(this);
    this.getRows = this.getRows.bind(this);
    this.getSize = this.getSize.bind(this);
    this.rowGetter = this.rowGetter.bind(this);
    this.handleGridSort = this.handleGridSort.bind(this);
    this.handleFilterMenuChange = this.handleFilterMenuChange.bind(this);
    this.handleClearFilters = this.handleClearFilters.bind(this);
    this.handleRowClick = this.handleRowClick.bind(this);
    this.toggleViewMode = this.toggleViewMode.bind(this);
    this.handleRefresh = this.handleRefresh.bind(this);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.getValidFilterValues = this.getValidFilterValues.bind(this);
    this.handleOnClearFilters = this.handleOnClearFilters.bind(this);
    this.resizeGrid = this.resizeGrid.bind(this);
    this.filterValues = this.filterValues.bind(this);
    this.validGrid = this.validGrid.bind(this);
    this.rowRenderer = this.rowRenderer.bind(this);
    this.getCellValue = this.getCellValue.bind(this);
    this.getLastNoteText = this.getLastNoteText.bind(this);
    this.getFilterRenderer = this.getFilterRenderer.bind(this);
    this.getNumItemsSelectedText = this.getNumItemsSelectedText.bind(this);
    this.handleClearFrontendFilters = this.handleClearFrontendFilters.bind(this);
    this.getGridColumns = this.getGridColumns.bind(this);
    this.hasFilters = this.hasFilters.bind(this);

    this.state = {
      isFetching: true,
      gridstate: {
        sortColumn: "",
        sortDirection: null,
        rows: [],
        originalRows: [],
        columns: [],
        filters: []
      },
      filterMenus: [],
      gridFilters: [],
      viewMode: VIEW_MODE_GRID,
      items: {},
      filters: {},
      rows: [],
      displaySearchListItems: false,
    };
    this.safetyRefresh = true;
  }

  UNSAFE_componentWillMount() {
    super.UNSAFE_componentWillMount();
    if (this.validGrid(this.props.rowType)) {
      let savedFilters = cloneDeep(this.props.clientSideFilters);
      for (let columnKey in savedFilters) {
        let filterObject = savedFilters[columnKey];
        if (
          !filterObject.filterValues &&
          Array.isArray(filterObject.filterTerm)
        ) {
          filterObject.filterValues = this.filterValues;
        }
      }
      this.setState({ filters: savedFilters });
    }
    if (this.props.rowType === "MAC") {
      this.setState({ items: this.props.caseListItems });
      if (this.props.caseListItems.rows && this.props.caseListItems.columns) {
        this.buildGrid(this.props.caseListItems);
      }
      if (this.props.gridFilters) {
        this.buildGridFilters(this.props.gridFilters);
      }
    }
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.rowType === "MAC") return;
    if (this.props.rowType === "Appraiser") this.handleOnClearFilters(); // clears frontend filters. If removed, frontend filters will stay but won't be displayed

    if (nextProps.searchText === "") {
      this.state.displaySearchListItems = false;
    } else if (this.props.searchListItems !== nextProps.searchListItems) {
      this.state.lastSearchText = nextProps.searchText;
      this.state.displaySearchListItems = true;
    }

    if (this.state.displaySearchListItems) {
      this.setState({ items: nextProps.searchListItems });
      if (nextProps.searchListItems.rows && nextProps.searchListItems.columns) {
        this.buildGrid(nextProps.searchListItems);
      }
    } else {
      this.setState({ items: nextProps.caseListItems });
      if (nextProps.caseListItems.rows && nextProps.caseListItems.columns) {
        this.buildGrid(nextProps.caseListItems);
      }
    }
    if (nextProps.gridFilters) {
      this.buildGridFilters(nextProps.gridFilters);
      if (this.safetyRefresh) {
        this.safetyRefresh = false;
        this.handleRefresh();
      }
    }
    if (nextProps.resizeRequired === true) {
      this.resizeGrid();
    }
  }

  componentWillUnmount() {
    this.setState({
      isFetching: true,
      lastGridFilters: this.state.gridFilters
    });
  }

  getCellValue(c, cellValue) {
    if (c.width === 0) return "";
    if (c.formatter === "Date") {
      return cellValue ? dateToStringYy(cellValue) : "";
    }
    if (c.formatter === "TimeSpan") {
      if (cellValue === "-1.00") {
        return "";
      }
    }
    else if (c.formatter === "Decimal") {
      return cellValue ? Number(cellValue).toFixed(2) : "0.00";
    } else if (c.key === "lastNote") {
      return (
        <TippedGridCell
          cellViewModel={cellValue}
          columnName={c.name}
        />
      );
    } else if (c.formatter === "TippedIcon") {
      switch (c.key) {
        case ("allowsEdits"): {
          return (
            <TippedIcon
              iconName="edit"
              tooltipText={cellValue ? "can edit" : "can't edit"}
              iconClassName={cellValue ? "opt-in-edits" : "no-opt-in-edits"}
              tooltipPopoutDirection="right"
            />
          );
        }
        default: break;
      }
    }
    return cellValue;
  }

  rowRenderer({ renderBaseRow, ...canvasProps }) {
    //Setting Elements that we may want to use in canvasProps
    //If I don't add this then we'll get extra classes of "undefined 'actualextraclasses'" when concatting
    canvasProps["extraClasses"] = "";

    //Elements of canvasProps
    const { idx, row } = canvasProps;

    //Other Useful Infomation
    let rowCount = this.getSize();

    //Must be the Last Row
    if (idx === rowCount - 1) {
      //Gotta trim as multiple extra classes could be applied
      canvasProps["extraClasses"] = (canvasProps["extraClasses"] + " row-footer").trim();
    }

    //Row is Locked
    if (row.lockedRaw) {
      canvasProps["extraClasses"] = (canvasProps["extraClasses"] + " row-locked").trim();
    }

    return renderBaseRow({ ...canvasProps });
  }

  handleRowClick(rowIdx, row) {
    if (row.locked === true) {
      return;
    }
    if (row && this.props.rowType !== "MAC") {
      let rowType = this.props.rowType;
      // If the row type column is specified and it has a value then use that row type
      if (this.props.rowTypeColumnName && row[this.props.rowTypeColumnName]) {
        rowType = row[this.props.rowTypeColumnName];
      }
      let pathname = "/case";
      switch (rowType) {
        case "Appraisal":
          pathname = "/case";
          break;
        case "Broker":
          pathname = "/broker-case";
          break;
        case "Insurance":
          pathname = "/case";
          break;
      }
      this.props.history.push({
        pathname: pathname,
        state: { controlNumber: row.controlIDNumber }
      });
    }
  }

  findFilterByTerm(term, filterList) {
    return filterList.find(element => {
      return element.term === term;
    });
  }

  resizeGrid() {
    //the react grid has resizing issues
    //see this if youd like https://github.com/adazzle/react-data-grid/issues/295
    //some people said to wrap it in two divs with position relative and then absolute, i did that and it didnt totally fix the issue
    //since resizing the window fixes the issue, i figured we could just send a resize even and make it fix itself, problem is a few resize events need to be sent for some stupid reason
    //this is the worst hack i have wrote in a while, i am going to javascript hell for this
    //if you ever find a better fix please let Jordan know what it was
    for (let i = 0; i < 10; i++) {
      setTimeout(() => window.dispatchEvent(new Event("resize")), i * 200);
    }
    this.props.resizeComplete();
  }

  renderCell(c, cellValue) {
    return this.getCellValue(c, cellValue);
  }

  buildGrid(items) {
    let columns = [],
      sortCol = null,
      sortDir = null;
    let visibleColumns = items.columns.filter(col => col.visible);
    columns = visibleColumns.map(({ key, name, sort, formatter, visible }) => {
      let cellClass;

      if (sort === true || sort === false) {
        sortCol = key;
        sortDir = sort ? "ASC" : "DESC";
      }

      /// cheap trick to set the column width based on column name and format
      let w = 200;
      if (formatter === "Date") {
        w = 90;
      }
      if (formatter === "Hide") {
        w = 0;
      }
      if (
        formatter === "Number" ||
        formatter === "Decimal" ||
        formatter === "TimeSpan" ||
        name.includes("#") ||
        formatter === "Negative-Red"
      ) {
        w = 110;
      }
      if (formatter === "Wide") {
        w = "50%";
      }
      if (formatter === "TippedIcon") {
        w = 100;
      }
      if (formatter === "Integer") {
        w = 80;
      }
      if (formatter && typeof formatter === "string" && formatter.includes("px")) {
        w = Number(formatter.replace("px", ""));
      }
      if (typeof formatter === "function" && formatter.name === "CELL_NEGATIVE_RED_FORMATTER") {
        w = 110;
      }
      if (key === "lastNote") w = 80;

      if (visible === false) {
        w = 0;
        name = "";
      }
      let filterable = this.props.rowTypeColumnName ? false : true;

      return {
        key,
        name,
        formatter,
        sortable: true,
        resizable: true,
        width: w,
        filterable: filterable,
        filterRenderer: this.getFilterRenderer(key),
        cellClass: cellClass
      };
    });

    //Disables Resizing Graph Column Widths
    columns.forEach(column => {
      column.resizable = true;
      if (column.key === "lastNote") {
        column.formatter = <CellFormatterTooltip />;
      }
    });

    let rows = [];
    items.rows.forEach(r => {
      let row = {};
      items.columns.forEach(c => {
        row[c.key + "Raw"] = r[c.key] == null ? "" : r[c.key];
        row[c.key] = this.renderCell(c, r[c.key] == null ? "" : r[c.key]);
      });
      rows.push(row);
    });

    this.setState({
      gridstate: {
        ...this.state.gridstate,
        columns: columns,
        rows: rows,
        originalRows: rows,
        sortColumn: sortCol,
        sortDirection: sortDir
      },
      rows: rows
    });
  }

  getFilterRenderer(key) {

    const SmartMultiSelectFilter = props => {
      return (
        <MultiSelectFilter
          {...props}
          filterValues={this.filterValues}
          isValidGrid={this.validGrid(this.props.rowType)}
        />
      );
    };
    const SmartSearchTextFilter = props => {
      return (
        <SearchTextFilter
          {...props}
          isValidGrid={this.validGrid(this.props.rowType)}
        />
      );
    };

    if (
      key === "inspectionType" ||
      key === "customerName" ||
      key === "customerNameNumber" ||
      key === "webStatus" ||
      key === "state" ||
      key === "appraisalStatus" ||
      key === "manager" ||
      key === "assignmentType"
    ) {
      return SmartMultiSelectFilter;
    } else {
      return SmartSearchTextFilter;
    }
  }

  buildGridFilters(newGridFilters) {
    if (this.props.rowTypeColumnName) {
      return;
    }
    if (this.props.isFetchingGridFilterMenus) {
      return <MSpinner />;
    }
    let menus = [];
    let filters = [];
    if (newGridFilters !== undefined && newGridFilters.length) {
      newGridFilters.forEach(g => {
        let h = { term: g.term, title: g.title, code: g.code, id: g.id };
        filters.push(h);

        if (this.props.isCustomerService) {
          if (
            g.term == "survey" ||
            g.term == "status" ||
            g.term == "insuranceCustomerList" ||
            g.term == "lenderCustomerList" ||
            g.term == "status_valuation"
          ) {
            menus.push(
              <CheckBoxMultiSelect
                onChange={this.handleFilterMenuChange}
                title={h.title}
                items={this.props.gridFilterMenus[h.term]}
                term={h.term}
              />
            );
          } else {
            menus.push(
              <InsuranceFilterMenu
                onChange={this.handleFilterMenuChange}
                title={h.title}
                items={this.props.gridFilterMenus[h.term]}
                term={h.term}
              />
            );
          }
        } else if (this.props.isAppraiserManager) {
          if (this.props.rowType !== "Appraiser") {
            if (
              g.term == "survey" ||
              g.term == "status" ||
              g.term == "customer" ||
              g.term == "status_valuation"
            ) {
              menus.push(
                <CheckBoxMultiSelect
                  onChange={this.handleFilterMenuChange}
                  title={h.title}
                  items={this.props.gridFilterMenus[h.term]}
                  term={h.term}
                />
              );
            } else {
              menus.push(
                <InsuranceFilterMenu
                  onChange={this.handleFilterMenuChange}
                  title={h.title}
                  items={this.props.gridFilterMenus[h.term]}
                  term={h.term}
                />
              );
            }
          } else if (g.term === "appraiser") {
            menus.push(
              <InsuranceFilterMenu
                onChange={this.handleFilterMenuChange}
                title={h.title}
                items={this.props.gridFilterMenus[h.term]}
                term={h.term}
              />
            );
          }
        } else if (this.props.isAppraiser) {
          if (g.term !== "customer") {
            if (g.term == "status_valuation") {
              menus.push(
                <CheckBoxMultiSelect
                  onChange={this.handleFilterMenuChange}
                  title={h.title}
                  items={this.props.gridFilterMenus[h.term]}
                  term={h.term}
                />
              );
            } else {
              menus.push(
                <InsuranceFilterMenu
                  onChange={this.handleFilterMenuChange}
                  title={h.title}
                  items={this.props.gridFilterMenus[h.term]}
                  term={h.term}
                />
              );
            }
          }
        } else {
          switch (g.term) {
            case "appraiser":
              if (
                this.props.isAppraiserManager ||
                this.props.rowType === "Appraiser"
              ) {
                menus.push(
                  <InsuranceFilterMenu
                    onChange={this.handleFilterMenuChange}
                    title={h.title}
                    items={this.props.gridFilterMenus[h.term]}
                    term={h.term}
                  />
                );
              }
              break;
            case "date":
              if (this.props.rowType !== "Appraiser") {
                menus.push(
                  <DateFilterMenu
                    onChange={this.handleFilterMenuChange}
                    title={h.title}
                  />
                );
              }
              break;
            case "status":
              if (this.props.rowType !== "Appraiser") {
                menus.push(
                  <StatusFilterMenu
                    onChange={this.handleFilterMenuChange}
                    title={h.title}
                    rowType={this.props.rowType}
                  />
                );
              }
              break;
            case "customer":
              if (
                this.props.rowType !== "Appraiser" &&
                this.props.isAppraiserManager
              ) {
                menus.push(
                  <CustomerFilterMenu
                    onChange={this.handleFilterMenuChange}
                    title={h.title}
                  />
                );
              }
              break;
            case "pay_period":
              if (this.props.rowType === "MAC") {
                menus.push(<InsuranceFilterMenu onChange={this.handleFilterMenuChange} title={h.title} items={this.props.gridFilterMenus[h.term]} term={h.term} />);
              }
              break;
          }
        }
      });
      this.setState({ filterMenus: menus, gridFilters: filters });
    }
  }

  downloadGrid() {
    // download the grid as an xlsx file
    // documentation https://github.com/SheetJS/js-xlsx#array-of-arrays-input
    const { rows, columns } = this.state.gridstate;

    // apply front-end filters
    const { filters } = this.state;
    let filteredRows = [...rows];
    if (filters) {
      for (let c in filters) {
        if (Array.isArray(filters[c].filterTerm)) {
          let filterList = [];
          filters[c].filterTerm.forEach(filterObject => {
            filterList.push(filterObject.value.toString().toUpperCase());
          });
          if (Array.isArray(filterList) && filterList.length) {
            filteredRows = filteredRows.filter(row => {
              return filterList.includes(row[c].toString().toUpperCase());
            });
          }
        } else {
          filteredRows = filteredRows.filter(row => {
            return row[c]
              .toString()
              .toUpperCase()
              .includes(filters[c].filterTerm.toString().toUpperCase());
          });
        }
      }
    }
    // build array of column keys
    // E.g. wsCols = [ "A", "B", "C" ];
    let wsCols = [];
    columns.forEach(c => {
      wsCols.push(c.key);
    });

    //build array of rows as one dynamic object per row
    // E.g. wsRows = [ { A:1, B:"two", C:"12/25/2017" }, { A:99, B:"test" ... } ];
    let wsRows = [];

    // header row
    let headerRow = {};
    columns.forEach(c => {
      headerRow[c.key] = c.name;
    });
    wsRows.push(headerRow);

    // all other rows
    filteredRows.forEach(r => {
      let row = {};
      columns.forEach(c => {
        c.value = "";
        // SD-6108 Collateral Connect - Unable to Download as Excel Spreadsheet
        if (c.key === "lastNote") row[c.key] = this.getLastNoteText(r);
        else row[c.key] = this.renderCell(c, r[c.key]);
      });
      wsRows.push(row);
    });

    // make a book, then a work sheet, then a download
    let wb = xlsx.utils.book_new();
    let ws = xlsx.utils.json_to_sheet(wsRows, {
      header: wsCols,
      skipHeader: true
    });
    xlsx.utils.book_append_sheet(wb, ws, "Sheet 1");
    xlsx.writeFile(wb, this.state.items.options.title + ".xlsx");
  }

  getLastNoteText(r) {
    if (r["lastNoteRaw"].value && r["lastNoteRaw"].value.noteBody) {
      return r["lastNoteRaw"].value.noteBody.trim().substring(0, 100);
    }
    return "";
  }

  toggleViewMode() {
    this.setState({
      viewMode:
        this.state.viewMode == VIEW_MODE_GRID ? VIEW_MODE_MAP : VIEW_MODE_GRID
    });
  }

  getRows() {
    return (
      filterRows(this.state)
    );
  }

  getSize() {
    return this.getRows().length;
  }

  // Check if any columns have filters enabled
  hasFilters() {
    return this.state.gridstate.columns.some(col => col.filterable);
  }

  // Build columns array for react-data-grid v7
  getGridColumns() {
    const columns = this.state.gridstate.columns.map(col => {
      const column = {
        key: col.key,
        name: col.name,
        width: col.width,
        resizable: col.resizable !== false,
        sortable: col.sortable !== false,
      };

      // Custom cell renderer for formatters
      if (col.formatter) {
        if (typeof col.formatter === 'function') {
          column.renderCell = ({ row, column: colDef }) => {
            const value = row[colDef.key];
            return col.formatter({ value, row });
          };
        } else if (React.isValidElement(col.formatter)) {
          // For React element formatters like CellFormatterTooltip
          column.renderCell = ({ row, column: colDef }) => {
            const value = row[colDef.key];
            return React.cloneElement(col.formatter, { value, row });
          };
        }
      }

      // Header renderer with filter support
      if (col.filterable && col.filterRenderer) {
        const HeaderWithFilter = () => (
          <div className="rdg-header-cell-content">
            <div className="rdg-header-cell-name">{col.name}</div>
            <div className="rdg-header-filter" onClick={(e) => e.stopPropagation()}>
              {typeof col.filterRenderer === 'function' 
                ? col.filterRenderer({ 
                    column: { key: col.key, name: col.name },
                    onChange: this.handleFilterChange,
                    getValidFilterValues: () => this.getValidFilterValues(col.key)
                  })
                : col.filterRenderer}
            </div>
          </div>
        );
        HeaderWithFilter.displayName = `HeaderWithFilter_${col.key}`;
        column.renderHeaderCell = HeaderWithFilter;
      }

      return column;
    });

    return columns;
  }

  rowGetter(rowIdx) {
    let rows = this.getRows();
    return (
      rows[rowIdx]
    );
  }

  filterValues(row, columnFilter, columnKey) {
    let include = true;
    if (columnFilter === null) {
      include = false;
    } else if (
      columnFilter.filterTerm &&
      !(
        Array.isArray(columnFilter.filterTerm) &&
        columnFilter.filterTerm.length === 0
      )
    ) {
      if (columnFilter.filterTerm.length) {
        include = columnFilter.filterTerm.some(filterTerm => {
          return (
            this.columnValueContainsSearchTerms(
              row[columnKey],
              filterTerm.value
            ) === true
          );
        });
      } else {
        include = this.columnValueContainsSearchTerms(
          row[columnKey],
          columnFilter.filterTerm.value
        );
      }
    }
    return include;
  }

  columnValueContainsSearchTerms(columnValue, filterTermValue) {
    if (columnValue !== undefined && filterTermValue !== undefined) {
      let strColumnValue = columnValue.toString();
      let strFilterTermValue = filterTermValue.toString();
      let checkValueIndex = strColumnValue
        .trim()
        .toLowerCase()
        .indexOf(strFilterTermValue.trim().toLowerCase());
      return (
        checkValueIndex !== -1 &&
        (checkValueIndex !== 0 || strColumnValue === strFilterTermValue)
      );
    }
    return false;
  }

  handleGridSort(sortColumn, sortDirection) {
    let key = sortColumn + "Raw";
    const compareText = (a, b) => {
      let x;
      let y;
      switch (key) {
        //I know what you're thinking, 'What the hell are unicode characters doing in here'
        //I found a character that comes very very far down the list of possible characters alphabetically to sort empty notes so they'll always end up at the bottom of the grid when sorting alphavetically ascending
        //The space on the opposite side of the ternary does the same thing but for when sorting alphabetically descending
        case "lastNoteRaw": {
          x = (a[key].value.noteBody ? a[key].value.noteBody.trim() : (sortDirection === "DESC" ? " " : "￭") + "").toUpperCase();
          y = (b[key].value.noteBody ? b[key].value.noteBody.trim() : (sortDirection === "DESC" ? " " : "￭") + "").toUpperCase();
          break;
        }
        default: {
          x = (a[key] + "").toUpperCase();
          y = (b[key] + "").toUpperCase();
          break;
        }
      }

      if (sortDirection === "ASC") {
        return x < y ? -1 : x > y ? 1 : 0;
      } else if (sortDirection === "DESC") {
        return x > y ? -1 : x < y ? 1 : 0;
      }
      return 0;
    };
    const compareNumbers = (a, b) => {
      let x = a[key] && parseFloat(a[key]);
      let y = b[key] && parseFloat(b[key]);
      if (sortDirection === "ASC") {
        return x < y ? -1 : x > y ? 1 : 0;
      } else if (sortDirection === "DESC") {
        return x > y ? -1 : x < y ? 1 : 0;
      }
      return 0;
    };

    let rows = [];
    let col = this.state.gridstate.columns.find(element => {
      if (element.key === sortColumn) return element;
    });
    if (sortDirection === "NONE") {
      rows = [...this.state.gridstate.originalRows];
    } else {
      let rowsToSort = [...this.state.gridstate.rows];
      let compareMethod =
        col && (col.formatter === "Decimal" || col.formatter === "Integer")
          ? compareNumbers
          : col && col.formatter === "Date"
            ? compareText
            : compareText;
      rows = rowsToSort.sort(compareMethod);
    }

    this.setState({
      gridstate: {
        ...this.state.gridstate,
        rows: rows,
        sortColumn: sortColumn,
        sortDirection: sortDirection
      },
      rows: rows
    });
  }

  handleClearFilters() {
    this.setState({
      gridstate: {
        ...this.state.gridstate,
        filters: []
      }
    });
  }

  handleFilterMenuChange(menuValue) {
    if (menuValue.term === "business") {
      this.props.getGridFilters(menuValue.code);
    } else {
      //TODO: use an immutable pattern here.
      let x = [...this.state.gridFilters];
      let updatedFilter = x.find(element => {
        return element.term === menuValue.term;
      });
      updatedFilter.title = menuValue.title;
      updatedFilter.id = menuValue.id;
      updatedFilter.code = menuValue.code;
      this.props.changeGridFilters(x);

      this.buildGridFilters(x);
      if (this.props.rowType === "MAC") {
        this.props.refreshCaseList(menuValue.title);
      } else {
        this.handleRefresh();
      }
    }
  }

  handleRefresh() {
    if (this.props.gridFilters) {
      if (this.state.displaySearchListItems || (this.props.rowType === "Appraiser" && this.props.searchText)) {
        this.props.refreshSearchList(this.props.searchText);
      } else {
        this.props.refreshCaseList();
      }
    }
  }

  handleFilterChange(filter) {
    let newFilters = Object.assign({}, this.state.filters);
    if (filter.filterTerm) {
      newFilters[filter.column.key] = filter;
    } else {
      delete newFilters[filter.column.key];
    }
    if (this.validGrid(this.props.rowType))
      this.props.saveClientSideFilters(newFilters);
    this.setState({ filters: newFilters });
  }

  getValidFilterValues(columnId) {
    let values = this.state.gridstate.rows.map(r => r[columnId]);
    let result = values.filter((item, i, a) => {
      return i === a.indexOf(item);
    });
    result = result.filter(Boolean);
    result.sort();
    return result;
  }

  handleOnClearFilters() {
    this.setState({ filters: {} });
  }

  validGrid(rowType) {
    return rowType === "Appraisal" ||
      rowType === "Insurance" ||
      rowType === "Broker"
      ? true
      : false;
  }

  getNumItemsSelectedText() {
    const totalRows = this.state.gridstate.rows.length;
    const showingRows = this.getSize();
    let showing = showingRows < totalRows ? `Showing ${commaFormat(showingRows)} of ` : "";
    return (
      <div>
        &nbsp;
        {`${showing}${commaFormat(totalRows)} Item${showingRows > 1 ? "s" : ""} Found`}
        {showing && this.props.rowType !== "MAC" && <a className="clear-filter-button" tabIndex="0" onClick={this.handleClearFrontendFilters}><i>Clear Filters</i></a>}
      </div>
    );
  }
  handleClearFrontendFilters() {
    this.setState({ filters: {} });
    this.props.clearClientSideFilters();
  }
  render() {
    if (
      this.props.isFetchingCaseList ||
      this.props.isFetchingSearchResults ||
      this.props.isFetchingGridFilterMenus ||
      Object.keys(this.state.items).length === 0
    ) {
      return <MSpinner />;
    }

    // NOTE: Height is controlled in the search.scss file.
    // See the .<outer> .react-grid-Grid {min-height: calc( 100vh - <offset>px) !important;}
    // class where
    //		.<outer> is a higher up class we can use to target this instance of the grid
    //    <offset>px is the verticle height taken up by the header, footer and other items.


    let additionalGridHeaderButtons = null;
    //If Additional Menu Buttons are defined then we offset the index here
    let renderMenuItems = this.state.filterMenus.map((item, index) => (
      <li key={additionalGridHeaderButtons ? index + additionalGridHeaderButtons.length : index} className="filterMenu server-side-filters">
        {item}
      </li>
    ));

    return (
      <div className="search-wrapper">
        <div
          className="description grid-header-description"
          data-test="description"
        >
          {this.props.rowType !== "MAC" && (<span className="message">
            <span className="grid-header-title">
              <span className="message-title">
                {this.state.items.options.title}
              </span>
            </span>
          </span>)}
          <span className="filterMenu">{renderMenuItems}</span>
          <span className="message">
            <span className="grid-header-results-string">
              {(this.state.displaySearchListItems && this.props.rowType !== "Appraiser")
                ? this.state.rows.length +
                ' results for "' +
                this.state.lastSearchText +
                '"'
                : this.getNumItemsSelectedText()}
            </span>
          </span>
          {this.props.rowType === "MAC" && (<span className="message">
            <span className="grid-header-title">
              <span className="message-title">
                {this.state.items.options.title}
              </span>
            </span>
          </span>)}
          <span className="grid-header-warning">
            {this.state.items.options.warning && (
              <span className="message-warning">
                {this.state.items.options.warning}
              </span>
            )}
          </span>
          <span className="grid-header-icon-list">
            <i
              className="md-icon material-icons"
              title="Refresh Grid"
              onClick={this.handleRefresh}
            >
              refresh
            </i>
            {!this.props.disableDownload ?
              <i
                className="md-icon material-icons"
                title="Download"
                onClick={this.downloadGrid}
              >
                cloud_download
            </i>
              : null
            }
            {this.props.mapDataForMap && (
              <i
                className="md-icon material-icons"
                title={
                  this.state.viewMode == VIEW_MODE_GRID
                    ? "Map View"
                    : "Grid View"
                }
                onClick={this.toggleViewMode}
              >
                {this.state.viewMode == VIEW_MODE_GRID ? "map" : "grid_on"}
              </i>
            )}
            {this.props.additionalGridHeaderButtons ?
              this.props.additionalGridHeaderButtons.map((icon, index) => {
                return (
                  <i
                    key={index}
                    className="md-icon material-icons"
                    title={icon.title}
                    onClick={icon.onClick}
                  >
                    {icon.icon}
                  </i>
                );
              })
              : null
            }
          </span>
        </div>

        {this.state.viewMode == VIEW_MODE_GRID ? (
          <ReactDataGrid
            columns={this.getGridColumns()}
            rows={this.getRows() || []}
            onCellClick={(args) => {
              if (this.handleRowClick) {
                this.handleRowClick(args.rowIdx, this.getRows()[args.rowIdx]);
              }
            }}
            onSortColumnsChange={(sortColumns) => {
              if (sortColumns.length > 0) {
                const { columnKey, direction } = sortColumns[0];
                this.handleGridSort(columnKey, direction);
              } else {
                this.handleGridSort(null, 'NONE');
              }
            }}
            sortColumns={this.state.gridstate.sortColumn ? [{ columnKey: this.state.gridstate.sortColumn, direction: this.state.gridstate.sortDirection }] : []}
            rowKeyGetter={(row, index) => index}
            defaultColumnOptions={{
              resizable: true,
              sortable: true,
            }}
            className="rdg-light fill-grid"
            style={{ height: '100%' }}
            rowClass={(row) => {
              const classes = [];
              // Last row styling
              const rowIdx = this.getRows().indexOf(row);
              if (rowIdx === this.getSize() - 1) {
                classes.push('row-footer');
              }
              // Locked row styling
              if (row.lockedRaw) {
                classes.push('row-locked');
              }
              return classes.length > 0 ? classes.join(' ') : undefined;
            }}
            headerRowHeight={this.hasFilters() ? 70 : 35}
          />
        ) : (
            <Map
              containerClass="map-control"
              loadingClass="map-control"
              mapClass="map-control"
              pins={this.props.mapDataForMap(this.state.gridstate.rows)}
            />
          )}
      </div>
    );
  }
}

ResultGrid.defaultProps = {
  disableDownload: false,
};

ResultGrid.propTypes = {
  caseListItems: PropTypes.object,
  searchListItems: PropTypes.object,
  isAppraiser: PropTypes.bool.isRequired,
  isAppraiserManager: PropTypes.bool.isRequired,
  rowType: PropTypes.string.isRequired,
  rowTypeColumnName: PropTypes.string, // The name of the column in a dataRow that contains a value to overrides the rowType setting

  history: PropTypes.object.isRequired,
  gridFilters: PropTypes.array.isRequired,
  refreshCaseList: PropTypes.func,
  refreshSearchList: PropTypes.func,
  changeGridFilters: PropTypes.func,
  mapDataForMap: PropTypes.func,
  isFetchingCaseList: PropTypes.bool,
  isFetchingSearchResults: PropTypes.bool,

  isFetchingGridFilterMenus: PropTypes.bool,
  gridFilterMenus: PropTypes.object,
  getGridFilters: PropTypes.func,
  gridMode: PropTypes.string,
  searchText: PropTypes.string,
  resizeRequired: PropTypes.bool,
  resizeComplete: PropTypes.func,
  clientSideFilters: PropTypes.object,
  saveClientSideFilters: PropTypes.func,
  additionalGridHeaderButtons: PropTypes.array,
  disableDownload: PropTypes.bool,
  clearClientSideFilters: PropTypes.func
};

ResultGrid.defaultPropss = {
  additionalGridHeaderButtons: null
};

export default withRouter(ResultGrid);
